import { useState } from "react";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import SaveIndicator from "@/design/components/SaveIndicator";
import InputNumeric from "@/design/forms/InputNumeric";
import { number } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import Valkyrie, { viSpinnerThird, viXmark } from "@sippy-platform/valkyrie";

import { useSortable } from "@dnd-kit/react/sortable";
import {
  IconButton,
  InputAdornment,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";

import groupMemberReducer from "./groupMemberReducer";

function MemberItem({ member, group, id, index, groupDispatch }) {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { ref, isDragging } = useSortable({ id, index });

  // Saving
  const {
    value: member2,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: member,
    reducer: groupMemberReducer,
    validator: () => ({
      ringSeconds: number()
        .required({ condition: group.type === "LinearGroup", message: "Ring seconds is required." })
        .min(1, {
          condition: group.type === "LinearGroup",
          message: "Ring seconds must be between 1 and 120 seconds.",
        })
        .max(120, {
          condition: group.type === "LinearGroup",
          message: "Ring seconds must be between 1 and 120 seconds.",
        }),
    }),
    onSubmit: () => Api.putGroupMember(group, member2),
  });

  // Handle changes
  const formHandler = (property, value) => {
    groupDispatch({ type: "updateMember", member, value });
    dispatch({ type: property, value });

    setSaveState(SaveState.UNSAVED);
  };

  const [pending, setPending] = useState(false);

  function deleteMember() {
    setPending(true);

    Promise.resolve(Api.deleteGroupMember(group, member))
      .then(() => groupDispatch({ type: "removeMember", member }))
      .finally(() => setPending(false));
  }

  return (
    <ListItem
      disablePadding
      ref={group.type === "LinearGroup" ? ref : undefined}
      secondaryAction={
        <Stack
          direction="row"
          sx={{
            gap: 0.5,
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          {group.type === "LinearGroup" && (
            <>
              <InputNumeric
                id="ringSeconds"
                name="ringSeconds"
                value={member2.ringSeconds}
                onChange={formHandler}
                min={1}
                max={120}
                endAdornment={
                  <InputAdornment position="end" sx={{ marginRight: 1 }}>
                    s
                  </InputAdornment>
                }
                sx={{ maxWidth: 90, backdropFilter: "blur(200px)" }}
                error={Object.keys(errors)[0] === "ringSeconds"}
                disabled={pending}
              />
              <SaveIndicator formState={formState} errors={allErrors} save={save} mini disabled={pending} />
            </>
          )}
          {matches && (
            <IconButton onClick={deleteMember} disabled={pending}>
              <Valkyrie icon={pending ? viSpinnerThird : viXmark} spin={pending} />
            </IconButton>
          )}
        </Stack>
      }
      sx={[
        {
          "&:nth-of-type(odd)": (theme) => ({
            backgroundColor: "#f8f8f8",
            ...theme.applyStyles("dark", {
              backgroundColor: "#28282d",
            }),
          }),
          borderRadius: 1,
        },
        group.type === "LinearGroup" && { cursor: "move" },
        group.type === "LinearGroup" && {
          "&:hover": (theme) => ({
            backgroundColor: "#efefef",
            ...theme.applyStyles("dark", {
              backgroundColor: "#323237",
            }),
          }),
        },
        isDragging && {
          boxShadow: 1,
          backdropFilter: "blur(5px)",
          backgroundColor: "rgb(248 248 248 / .5) !important",
          ...theme.applyStyles("dark", {
            backgroundColor: "rgb(40 40 45 / .5) !important",
          }),
          position: "relative",
          zIndex: 1,
        },
      ]}
    >
      {group.type === "LinearGroup" && (
        <ListItemIcon sx={{ ml: 1.5, minWidth: "3.5ch" }}>{member.position}</ListItemIcon>
      )}
      <ListItemAvatar sx={[group.type !== "LinearGroup" && { ml: 1.5 }]}>
        <Avatar text={member2.sippeer.name} />
      </ListItemAvatar>
      <ListItemText primary={member2.sippeer.name} secondary={member2.sippeer.exten} />
    </ListItem>
  );
}

MemberItem.propTypes = {
  member: PropTypes.object,
  group: PropTypes.object,
  id: PropTypes.number,
  index: PropTypes.number,
  groupDispatch: PropTypes.func,
};

export default MemberItem;
